import { CanvasLayer } from 'leaflet-canvas-layer'
import L from 'leaflet'
import { GPS } from '@map-sdk/sdk/utils/GPS'
import { PointClusterer } from '@map-sdk/sdk/utils/Dilution/PointClusterer'
import { CanvasEventHandler } from '@map-sdk/sdk/plugins/CanvasEventHandler'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface IPortData {
  id: string
  lat: number
  lng: number
  nameCN?: string
  nameEN?: string
  range?: number[]
  flagDraw?: boolean
  x: number
  y: number
  label?: string
  rnd: number
}

interface IPortCanvasOptions {
  [key: string]: any
}

export class PortCanvas {
  private _imgWidth = 16
  private _imgHeight = 16
  private _map: any // 假设map是一个地图对象，具体类型根据实际情况定义
  private _canvas!: HTMLCanvasElement
  private _portImg: HTMLImageElement
  private _zoomLabel = 8

  private portData: IPortData[] = []
  private villagePortData: IPortData[] = [] // 抽稀之后的点
  private canvasLayer: CanvasLayer
  constructor(map: MyMap, options: IPortCanvasOptions = {}) {
    this._map = map
    this._portImg = new Image()
    this._portImg.src = `https://api.iconify.design/ph:anchor-bold.svg?color=%23ff0000`
    this.canvasLayer = new CanvasLayer().delegate(this).addTo(this._map)
    this._map.on('zoomend moveend', () => {
      requestAnimationFrame(() => this.refreshPointClusterer())
    })
  }
  protected onDrawLayer(info: { canvas: HTMLCanvasElement }): void {
    this._canvas = info.canvas
    this._canvas.classList.add('port-canvas')
    new CanvasEventHandler(info.canvas, {
      mousedown: this.handleCanvasClick.bind(this),
      mousemove: this.handleCanvasClick.bind(this),
    })
  }
  private format() {
    this.portData = this.portData.map((item) => {
      const { x, y } = this._map.latLngToContainerPoint(
        GPS.latLngToGPSBymap([item.lat, item.lng], this._map)
      )
      return {
        ...item,
        x,
        y,
        label: item.nameCN || item.nameEN,
        flagDraw: true,
      }
    })
  }
  public show(methods: (arg0: (data: IPortData[]) => void) => any) {
    methods &&
      methods((data: IPortData[]) => {
        this.portData = data
        requestAnimationFrame(() => this.refreshPointClusterer())
      })
  }
  public hiden() {
    this.portData = []
    this.canvasLayer.remove()
    requestAnimationFrame(() => this.refreshPointClusterer())
  }
  private isNearPoint(
    mouseX: number,
    mouseY: number,
    point: IPortData,
    threshold = 5
  ) {
    const { x, y } = point
    if (x && y) {
      const dx = Math.abs(mouseX - x)
      const dy = Math.abs(mouseY - y)
      return dx <= threshold && dy <= threshold
    }
    return false
  }
  private handleCanvasClick(event: {
    clientX: number
    clientY: number
    type: string
  }) {
    const rect = this._canvas.getBoundingClientRect()
    const mouseX = event.clientX - rect.left
    const mouseY = event.clientY - rect.top

    for (const point of this.villagePortData) {
      if (this.isNearPoint(mouseX, mouseY, point)) {
        // 鼠标靠近该点，准备显示提示框
        if (event.type === 'mousemove') {
          this._canvas.classList.add('leaflet-interactive')
        }
        this.showTooltip(point)
        return
      }
    }
    this._canvas.classList.remove('leaflet-interactive')
    // 鼠标未靠近任何点，隐藏提示框
    this.hideTooltip()
  }

  /**
   * 绘制相关点
   * @returns
   */
  private drawPorts(portData: IPortData[]) {
    if (!this._canvas) {
      return
    }

    const ctx = this._canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, this._canvas.width, this._canvas.height)
    for (const port of portData) {
      // 绘制港口图标，这里仅作示例，您可以根据实际需求替换为具体的绘制逻辑
      // ctx.beginPath()
      // ctx.arc(port.x, port.y, 5, 0, 2 * Math.PI) // 以 (x, y) 为中心绘制半径为 5 的圆
      // ctx.fillStyle = 'rgba(255, 0, 0, 0.8)' // 红色半透明填充
      // ctx.fill()
      // ctx.strokeStyle = 'white'
      // ctx.lineWidth = 2
      // ctx.stroke()
      ctx.save()
      ctx.fillStyle = 'rgba(255, 255, 255, 0)'
      ctx.translate(port.x, port.y)
      ctx.drawImage(
        this._portImg,
        -this._imgWidth / 2,
        -this._imgHeight / 2,
        this._imgWidth,
        this._imgHeight
      )
      // 复原画布
      ctx.restore()
      this.drawPortLabel(ctx, port)
    }
  }

  private drawPortLabel(
    ctx: CanvasRenderingContext2D,
    portData: IPortData
  ): void {
    const zoom = this._map.getZoom()
    const { x, y, label } = portData
    if (!ctx || zoom < this._zoomLabel || !portData.flagDraw) {
      return
    }
    if (x && y) {
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x + 10, y - 10)
      ctx.strokeStyle = '#000000'
      ctx.globalAlpha = 0.5
      ctx.lineWidth = 1
      ctx.stroke()
      ctx.closePath()
      const textWidth = ctx.measureText(label!).width
      ctx.moveTo(x, y)
      ctx.globalAlpha = 0.7
      ctx.fillStyle = '#FFF'
      ctx.fillRect(x + 10, y - 25, textWidth + 10, 20)
      ctx.globalAlpha = 1.0
      ctx.font = '14px Arial'
      ctx.fillStyle = '#000'
      ctx.fillText(label!, x + 15, y - 10, textWidth)
    }
  }

  private showTooltip(point: IPortData) {
    const leafletPopupPane = document.querySelector(
      '.leaflet-popup-pane'
    ) as HTMLDivElement
    leafletPopupPane.innerHTML = `<div class="portTitle" style="left: ${point.x}px; top: ${point.y}px;
            position: absolute;
            font-size: 12px;
            padding: 0 5px;
            margin: 10px;
            background: #fff;
            white-space: nowrap;">
            <span>${point.label}</span>
            </div>`
  }
  private hideTooltip() {
    const leafletPopupPane = document.querySelector(
      '.portTitle'
    ) as HTMLDivElement
    leafletPopupPane && leafletPopupPane.remove()
  }

  refreshPointClusterer() {
    const map = this._map
    this.format()
    // 稀疏算法
    new PointClusterer(
      map,
      this.portData.map((item) => ({
        ...item,
        point: new L.LatLng(item.lat, item.lng),
      })),
      {
        thresholdFactor: 0.03,
        maxPointRange: 0.5,
        collisionResolution: 'nearest',
        callback: (params: any) => {
          this.villagePortData = params
          this.drawPorts(params)
        },
      }
    )
  }
}
